import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown, Plus, Menu, X, CheckCircle } from 'lucide-react';
import { db } from './firebase-config';
import { collection, getDocs } from "firebase/firestore"; 
import './App.css';
import { Spinner, SkeletonEventGrid } from './components/Spinner';
import EventCard from './EventCard';
import PostPage from './PostPage';
import EventDetailsPage from './EventDetailsPage';
import FAQPage from './FAQPage';
import CommunityDetailPage from './CommunityDetailPage';
import SubscribePage from './SubscribePage';
import AboutPage from './AboutPage';
import ErrorPage from './ErrorPage';

const useHorizontalScroll = ({ autoScroll = false, allowWheelScroll = true } = {}) => {
  const elRef = useRef();
  const animationId = useRef();
  const isPaused = useRef(false);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;

    const scrollSpeed = 1;

    const animate = () => {
      if (!isPaused.current && !isDragging.current && autoScroll) {
        el.scrollLeft += scrollSpeed;

        if (el.scrollLeft >= el.scrollWidth / 2) {
          el.scrollLeft = 0;
        }
      }
      animationId.current = requestAnimationFrame(animate);
    };

    if (autoScroll) {
      animationId.current = requestAnimationFrame(animate);
    }

    const handleMouseDown = (e) => {
      isDragging.current = true;
      startX.current = e.pageX;
      scrollLeft.current = el.scrollLeft;
    };

    const handleMouseMove = (e) => {
      if (!isDragging.current) return;
      const delta = startX.current - e.pageX;
      el.scrollLeft = scrollLeft.current + delta;
    };

    const handleMouseUp = () => {
      isDragging.current = false;
    };

    const handleTouchStart = (e) => {
      isDragging.current = true;
      startX.current = e.touches[0].pageX;
      scrollLeft.current = el.scrollLeft;
    };

    const handleTouchMove = (e) => {
      if (!isDragging.current) return;
      const delta = startX.current - e.touches[0].pageX;
      el.scrollLeft = scrollLeft.current + delta;
      if (Math.abs(delta) > 5) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = () => {
      isDragging.current = false;
    };

    const handleWheel = (e) => {
      if (!allowWheelScroll) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };

    const handleMouseEnter = () => {
      isPaused.current = true;
    };

    const handleMouseLeave = () => {
      isPaused.current = false;
    };

    el.style.userSelect = 'none';

    el.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd);

    if (allowWheelScroll) {
      el.addEventListener('wheel', handleWheel, { passive: false });
    }

    el.addEventListener('mouseenter', handleMouseEnter);
    el.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      cancelAnimationFrame(animationId.current);

      el.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);

      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);

      if (allowWheelScroll) {
        el.removeEventListener('wheel', handleWheel);
      }

      el.removeEventListener('mouseenter', handleMouseEnter);
      el.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [autoScroll, allowWheelScroll]);

  return elRef;
};

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const toggleMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  return (
    <div className="max-w-7xl mx-auto p-6">
      <nav className="flex justify-between items-center mb-10">
        <div className="flex items-center">
          <Link to="/" className="flex items-center space-x-2">
            <img src="/logo.svg" alt="Eventzy Logo" className="hidden md:!block w-40 h-auto" />
            <img src="/logo-mobile.svg" alt="Eventzy Icon" className="block md:hidden w-10 h-10" />
          </Link>
        </div>
        <div className="flex items-center space-x-4">
          <div className="hidden md:!flex items-center space-x-8">
            <NavLink
              to="/events"
              className={({ isActive }) =>
                `text-lg font-medium transition-colors duration-200 ${isActive ? 'text-[#2696E8]' : 'text-gray-200'} hover:text-[#2696E8]`
              }
            >
              Events
            </NavLink>
            <NavLink
              to="/communities"
              className={({ isActive }) =>
                `text-lg font-medium transition-colors duration-200 ${isActive ? 'text-[#2696E8]' : 'text-gray-200'} hover:text-[#2696E8]`
              }
            >
              Communities
            </NavLink>
            <NavLink
              to="/subscribe"
              className={({ isActive }) =>
                `text-lg font-medium transition-colors duration-200 ${isActive ? 'text-[#2696E8]' : 'text-gray-200'} hover:text-[#2696E8]`
              }
            >
              Subscribe
            </NavLink>
            <a
              href="/post"
              className="bg-gradient-to-r from-blue-500 to-cyan-400 text-white px-6 py-2 rounded-full font-medium hover:from-blue-600 hover:to-cyan-500 transition-all duration-200 shadow-md"
            >
              Share an Event
            </a>
          </div>
          <button className="md:hidden p-2 text-gray-200" onClick={toggleMenu}>
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        <div
          className={`absolute top-16 right-6 w-48 bg-[#14181D] shadow-lg rounded-lg p-4 transition-all duration-300 ${isMobileMenuOpen ? 'block' : 'hidden'} md:hidden`}
        >
          <NavLink
            to="/events"
            className={({ isActive }) =>
              `block py-2 text-lg font-medium transition-colors duration-200 ${isActive ? 'text-blue-500' : 'text-gray-200'} hover:text-blue-400`
            }
            onClick={toggleMenu}
          >
            Events
          </NavLink>
          <NavLink
            to="/communities"
            className={({ isActive }) =>
              `block py-2 text-lg font-medium transition-colors duration-200 ${isActive ? 'text-blue-500' : 'text-gray-200'} hover:text-blue-400`
            }
            onClick={toggleMenu}
          >
            Communities
          </NavLink>
          <NavLink
            to="/subscribe"
            className={({ isActive }) =>
              `block py-2 text-lg font-medium transition-colors duration-200 ${isActive ? 'text-blue-500' : 'text-gray-200'} hover:text-blue-400`
            }
            onClick={toggleMenu}
          >
            Subscribe
          </NavLink>
          <NavLink
            to="/faq"
            className={({ isActive }) =>
              `block py-2 text-lg font-medium transition-colors duration-200 ${isActive ? 'text-blue-500' : 'text-gray-200'} hover:text-blue-400`
            }
            onClick={toggleMenu}
          >
            FAQ
          </NavLink>
          <NavLink
            to="/post"
            className={({ isActive }) =>
              `block py-2 text-lg font-medium transition-colors duration-200 ${isActive ? 'text-blue-500' : 'text-gray-200'} hover:text-blue-400`
            }
            onClick={toggleMenu}
          >
            Post an Event
          </NavLink>          
        </div>
      </nav>
    </div>
  );
};

const Footer = () => (
  <footer className="mt-12 py-8 bg-[#14181D] text-center text-gray-400">
  <div className="max-w-7xl mx-auto px-6">
    <div className="mb-4 flex items-center justify-center">
      <img 
        src="/logo-mobile.svg" 
        alt="Eventzy Logo"
        className="w-9 h-9 mr-3 animate-spin-slow"
      />
      <span className="text-2xl font-bold animate-gradient-text">Eventzy</span>
    </div>
      <div className="flex justify-center space-x-6 mb-4">
        <a href="/about" className="hover:text-blue-400 text-gray-400 font-medium">
          About
        </a>
        <a href="mailto:designerlk@proton.me" className="hover:text-blue-400 text-gray-400 font-medium">
          Contact Us
        </a>
        <a href="/faq" className="hover:text-blue-400 text-gray-400 font-medium">
          FAQ
        </a>
      </div>
      <p className="text-sm text-gray-400">
        © {new Date().getFullYear()} Eventzy. Made with ❤️
      </p>
    </div>
  </footer>
);

const HeroSection = () => {
  return (
    <div className="min-h-[60vh] flex flex-col justify-center items-center text-center px-4">
      <h1 className="text-4xl md:text-6xl font-extrabold animate-gradient-text leading-tight">
        <span className="block">Discover Free Opportunities</span>
        <span className="block mt-2 md:mt-0">in Sri Lanka</span>
      </h1>
      <p className="text-lg md:text-xl text-gray-200 mt-4 max-w-xl">
      Connect with events and programs that are free to join and full of potential
      </p>
      <div className="mt-6 flex gap-4 justify-center flex-wrap">
        <a
          href="/events"
          className="px-8 py-3 sm:px-6 sm:py-2 text-white font-semibold text-lg sm:text-base rounded-full bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 transition-all duration-200 shadow-lg transform hover:scale-105 flex items-center justify-center gap-2"
        >
          Join an Event
          <ChevronRight size={24} />
        </a>
        <a
          href="/post"
          className="px-8 py-3 sm:px-6 sm:py-2 text-white font-semibold text-lg sm:text-base rounded-full border-2 border-white bg-transparent text-white hover:bg-gradient-to-r hover:from-blue-500 hover:to-cyan-400  transition-all duration-200 transform hover:scale-105 flex items-center justify-center gap-2"
        >
          <Plus size={20} />
          Post Your Event
        </a>
      </div>
    </div>
  );
};

const MainPage = ({ events, communities, loading }) => {
  const topEventsRef = useHorizontalScroll({ autoScroll: true, allowWheelScroll: false }); 
  const communitiesRef = useHorizontalScroll({ autoScroll: true, allowWheelScroll: false });

  const renderEventCards = (eventList) =>
    eventList.map((event, index) => (
      <div key={`event-${event.id}-${index}`} className="flex-shrink-0 mx-4 min-w-[250px]">
        <EventCard event={event} />
      </div>
    ));

    const renderCommunityCards = (communityList) =>
      communityList.map((community, index) => (
        <div
          key={`community-${community.id}-${index}`}
          className="flex-shrink-0 w-64 bg-[#14181D] p-6 rounded-xl shadow-md m-4 text-center hover:shadow-xl transition-shadow duration-200"
        >
          <img
            src={community.avatar || '/placeholder-avatar.svg'}
            alt={community.name}
            className="w-24 h-24 rounded-full mx-auto mb-4"
          />
          <div className="flex items-center justify-center mb-2">
            <h3 className="text-xl font-bold text-white">{community.name}</h3>
            {[4, 5, 6].includes(community.verified) && (
              <span
                className={`ml-1 ${
                  community.verified === 4
                    ? 'verified-badge-user'
                    : community.verified === 5
                    ? 'verified-badge-admin'
                    : community.verified === 6
                    ? 'verified-badge-community'
                    : ''
                }`}
              >
                <CheckCircle className="text-blue-500" size={14} />
              </span>
            )}
          </div>
          <p className="text-gray-200 mt-2 overflow-hidden text-ellipsis line-clamp-3">
            {community.description}
          </p>
        </div>
      ));
    

  return (
    <div className="max-w-7xl mx-auto p-6">
      <HeroSection />
      <section className="mb-12">
        <h2 className="text-3xl font-bold mb-6 text-white">Top Events</h2>
        <div ref={topEventsRef} className="flex overflow-x-hidden scrollbar-hide py-4 top-events">
          {loading ? <SkeletonEventGrid /> : renderEventCards(events)}
          {loading ? null : renderEventCards(events)}
        </div>
      </section>

      <section className="mb-12">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-white">All Upcoming Events</h2>
          <Link to="/events" className="text-blue-400 hover:text-[#2696E8]">
            See all
          </Link>
        </div>
        {loading ? <SkeletonEventGrid /> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center lg:justify-items-start">
            {events.slice(0, 6).map((event) => (
              <EventCard key={`event-main-${event.id}`} event={event} />
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-white">Communities</h2>
          <Link to="/communities" className="text-blue-400 hover:text-[#2696E8]">
            See all
          </Link>
        </div>
        <div ref={communitiesRef} className="flex overflow-x-hidden scrollbar-hide py-4 communities">
          {loading ? <SkeletonEventGrid /> : renderCommunityCards(communities)}
          {loading ? null : renderCommunityCards(communities)}
        </div>
      </section>
    </div>
  );
};

const EventsPage = ({ events, loading }) => {
  const [filter, setFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const eventsPerPage = 9;

  const categories = ['All', 'Event', 'Workshop', 'Volunteer', 'Meet Up', 'Seminar', 'Program'];
  const filteredEvents = filter === 'All' ? events : events.filter((event) => event.type === filter);

  const indexOfLastEvent = currentPage * eventsPerPage;
  const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
  const currentEvents = filteredEvents.slice(indexOfFirstEvent, indexOfLastEvent);
  const totalPages = Math.ceil(filteredEvents.length / eventsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <section className="mb-12">
        <h2 className="text-3xl font-bold mb-6 text-white">All Events</h2>
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => {
                setFilter(category);
                setCurrentPage(1);
              }}
              className={`px-4 py-2 rounded-full text-sm font-semibold uppercase transition-colors duration-200 ${filter === category ? 'bg-gradient-to-r from-blue-500 to-cyan-400 text-white' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'}`}
            >
              {category}
            </button>
          ))}
        </div>
        {loading ? (
          <SkeletonEventGrid />
        ) : (
        <>
          {filteredEvents.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-6">
              <img src="/Notfound.svg" alt="No events" className="w-48 h-48 object-cover mb-4" />
              <p className="text-gray-200">No events available under this category.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center lg:justify-items-start">
                {currentEvents.map((event) => (
                  <EventCard key={`event-page-${event.id}`} event={event} />
                ))}
              </div>
              <div className="flex items-center justify-center space-x-2 mt-6">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="w-9 h-9 flex items-center justify-center bg-gray-800 text-white rounded-full disabled:opacity-40 hover:bg-blue-600 transition"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="text-sm text-gray-300 font-medium px-3 py-1 rounded-full bg-[#1F2937]">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="w-9 h-9 flex items-center justify-center bg-gray-800 text-white rounded-full disabled:opacity-40 hover:bg-blue-600 transition"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            </>
          )}
        </>
        )}
      </section>
    </div>
  );
};

const CommunitiesPage = ({ communities, loading }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('Most Posts');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const communitiesPerPage = 9;

  const sortedCommunities = [...communities].sort((a, b) => {
    if (sortBy === 'Most Posts') {
      return (b.post || 0) - (a.post || 0);
    } else if (sortBy === 'Name A-Z') {
      return a.name.localeCompare(b.name);
    }
    return 0;
  });

  const indexOfLastCommunity = currentPage * communitiesPerPage;
  const indexOfFirstCommunity = indexOfLastCommunity - communitiesPerPage;
  const currentCommunities = sortedCommunities.slice(indexOfFirstCommunity, indexOfLastCommunity);
  const totalPages = Math.ceil(sortedCommunities.length / communitiesPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <section>
        <div className="flex justify-between items-center mb-6 relative">
          <h2 className="text-3xl font-bold text-white">Communities</h2>

          {/* Custom dropdown */}
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="inline-flex justify-center items-center gap-1 px-4 py-2 text-sm font-medium text-blue-300 hover:text-[#2696E8] focus:outline-none"
            >
              Sort by: {sortBy}
              <ChevronDown className="w-4 h-4" />
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-md shadow-lg bg-[#1F2937] ring-1 ring-black ring-opacity-5">
                <button
                  onClick={() => {
                    setSortBy('Most Posts');
                    setDropdownOpen(false);
                    setCurrentPage(1);
                  }}
                  className={`block w-full px-4 py-2 text-sm text-gray-200 text-left hover:bg-gray-700 ${
                    sortBy === 'Most Posts' ? 'bg-gray-700 font-semibold' : ''
                  }`}
                >
                  Most Posts
                </button>
                <button
                  onClick={() => {
                    setSortBy('Name A-Z');
                    setDropdownOpen(false);
                    setCurrentPage(1);
                  }}
                  className={`block w-full px-4 py-2 text-sm text-gray-200 text-left hover:bg-gray-700 ${
                    sortBy === 'Name A-Z' ? 'bg-gray-700 font-semibold' : ''
                  }`}
                >
                  Name A-Z
                </button>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <SkeletonEventGrid />
        ) : (
          <>
            {currentCommunities.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-6">
                <img src="/Notfound.svg" alt="No communities" className="w-48 h-48 object-cover mb-4" />
                <p className="text-gray-200">No communities found.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center lg:justify-items-start">
                  {currentCommunities.map((community) => (
                    <Link
                      to={`/communities/${community.id}`}
                      key={`community-page-${community.id}`}
                      className="flex-shrink-0 w-full max-w-[350px] bg-[#14181D] p-6 rounded-xl shadow-md text-center hover:shadow-xl transition-shadow duration-200"
                    >
                      <img
                        src={community.avatar || '/placeholder-avatar.svg'}
                        alt={community.name}
                        className="w-24 h-24 rounded-full mx-auto mb-4"
                      />
                      <div className="flex items-center justify-center mb-2">
                        <h3 className="text-xl font-bold text-white">{community.name}</h3>
                        {[4, 5, 6].includes(community.verified) && (
                          <span className={`ml-1 ${
                            community.verified === 4
                              ? 'verified-badge-user'
                              : community.verified === 5
                              ? 'verified-badge-admin'
                              : 'verified-badge-community'
                          }`}>
                            <CheckCircle className="text-blue-500" size={14} />
                          </span>
                        )}
                      </div>
                      <p className="text-gray-200 mt-2 overflow-hidden text-ellipsis line-clamp-3">
                        {community.description}
                      </p>
                      <button className="mt-4 border border-blue-600 text-blue-600 px-4 py-2 rounded-full hover:bg-blue-700 hover:text-white font-medium transition-colors duration-200">
                        View {community.verified === 1 || community.verified === 4
                          ? 'User'
                          : community.verified === 2 || community.verified === 5
                          ? 'Community'
                          : 'Admin'}
                      </button>
                    </Link>
                  ))}
                </div>
                <div className="flex items-center justify-center space-x-2 mt-6">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className="w-9 h-9 flex items-center justify-center bg-gray-800 text-white rounded-full disabled:opacity-40 hover:bg-blue-600 transition"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <span className="text-sm text-gray-300 font-medium px-3 py-1 rounded-full bg-[#1F2937]">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 flex items-center justify-center bg-gray-800 text-white rounded-full disabled:opacity-40 hover:bg-blue-600 transition"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
};

const NotFoundPage = () => (
  <div className="flex flex-col justify-center items-center text-center px-4 mt-0">
    <h1 className="text-4xl md:text-6xl font-extrabold animate-gradient-text leading-tight mb-6">
    Page Not Found
    </h1>
    <img src="/404-desktop.png" alt="404 Not Found" className="hidden md:!block w-full max-w-lg mb-6" />
    <img src="/404-mobile.png" alt="404 Not Found" className="block md:!hidden w-full max-w-sm mb-6" />
    <p className="text-lg text-gray-200 mb-6">
      The page you're looking for doesn't exist or has been moved.
    </p>
    <Link
      to="/"
      className="px-6 py-3 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors font-medium"
    >
      Back to Home
    </Link>
  </div>
);

const AppContent = ({ events, communities, loading, error }) => {
  const location = useLocation();

  const shouldShowFAQ = () => {
    const path = location.pathname;
    return path === '/' || path.startsWith('/events/');
  };

  if (loading) return <Spinner />;
  if (error) return <ErrorPage message={error} onRetry={() => window.location.reload()} />;

  return (
    <div className="min-h-screen bg-[#0F1216] text-white font-poppins">
      <Header />
      <Routes>
        <Route path="/" element={<MainPage events={events} communities={communities} loading={loading} />} />
        <Route path="/events" element={<EventsPage events={events} loading={loading} />} />
        <Route path="/communities" element={<CommunitiesPage communities={communities} loading={loading} />} />
        <Route path="/communities/:id" element={<CommunityDetailPage communities={communities} />} />
        <Route path="/post" element={<PostPage />} />
        <Route path="/events/:id" element={<EventDetailsPage events={events} />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/subscribe" element={<SubscribePage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      {shouldShowFAQ() && <FAQPage />}
      <Footer />
    </div>
  );
};

function App() {
  const [events, setEvents] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const eventsRef = collection(db, "events");
        const eventsSnapshot = await getDocs(eventsRef);
        const eventsList = eventsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const filteredEvents = eventsList
          .filter((event) => {
            if (!event.date) return false;
            const eventDate = typeof event.date === 'string' ? new Date(event.date) : new Date(event.date);
            return eventDate >= today;
          })
          .sort((a, b) => {
            const dateA = typeof a.date === 'string' ? new Date(a.date) : new Date(a.date);
            const dateB = typeof b.date === 'string' ? new Date(b.date) : new Date(b.date);
            return dateA - dateB;
          });

        setEvents(filteredEvents);

        const communitiesRef = collection(db, "communities");
        const communitiesSnapshot = await getDocs(communitiesRef);
        const communitiesList = communitiesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCommunities(communitiesList);
      } catch (error) {
        setError("Failed to load data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <Router>
      <AppContent
        events={events}
        communities={communities}
        loading={loading}
        error={error}
      />
    </Router>
  );
}

export default App;